import React, { useEffect, useState } from 'react';
import { projectMilestoneServices } from '../../../APIServices/projectMilestoneServices';
import Pagination from '../../../hooks/Pagination';
import AddNewProjectMilestone from './AddNewProjectMilestone';
import UpdateProjectMilestone from './UpdateProjectMilestone';
import DeleteProjectMilestone from './DeleteProjectMilestone';
import DeletedProjectMilestoneHistory from './DeletedProjectMilestoneHistory';

function ProjectMilestoneManagement() {
  const [projectMilestoneList, setProjectMilestoneList] = useState([]);
  const [projectMilestoneCount, setProjectMilestoneCount] = useState(0);
  const [deleteProjectMilestoneList, setDeleteProjectMilestoneList] = useState([]);
  const [deleteProjectMilestoneCount, setDeleteProjectMilestoneCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [projectMilestoneData, setProjectMilestoneData] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const totalPage = Math.ceil(projectMilestoneCount / itemsPerPage);
  const [deletedPageNumber, setDeletedPageNumber] = useState(1);
  const [deletedItemsPerPage, setDeletedItemsPerPage] = useState(10);

  const getProjectMilestoneData = async () => {
    setLoading(true);
    const data = await projectMilestoneServices.getProjectMilestoneData('active', '', pageNumber, itemsPerPage);
    setProjectMilestoneList(data?.data || []);
    setProjectMilestoneCount(data?.count || 0);
    setLoading(false);
  };

  const getDeletedProjectMilestoneData = async () => {
    setLoading(true);
    const data = await projectMilestoneServices.getProjectMilestoneData('deleted', '', deletedPageNumber, deletedItemsPerPage);
    setDeleteProjectMilestoneList(data?.data || []);
    setDeleteProjectMilestoneCount(data?.count || 0);
    setLoading(false);
  };

  useEffect(() => {
    getProjectMilestoneData();
  }, [pageNumber, itemsPerPage]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Workspace metadata</div>
          <h1 className="page-title">Project Milestone Management</h1>
          <p className="page-sub">
            Create, update, and manage project milestone names used in project tracking.
          </p>
        </div>
      </div>

      <div className="flex between center mb-3">
        <div className="section-label">
          Project Milestones <span className="count">{projectMilestoneCount}</span>
        </div>

        <div className="hstack gap-4">
          <button className="btn btn-primary" onClick={() => setModalOpen('add')}>
            <i className="ti ti-plus" />
            Add Project Milestone
          </button>
          <button className="btn btn-danger" onClick={() => setModalOpen('deletedHistiory')}>
            <i className="ti ti-history" />
            Deleted History
          </button>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <div className="buttonLoader" />
            <div style={{ marginTop: 12 }}>Loading project milestones...</div>
          </div>
        ) : projectMilestoneCount === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
            <i className="ti ti-timeline-event" style={{ fontSize: 24, display: 'block', marginBottom: 8 }} />
            No Project Milestone. Click "Add Project Milestone" to create one.
          </div>
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Project Milestone</th>
                  <th>Default Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {projectMilestoneList.map((item) => (
                  <tr key={item._id}>
                    <td className="medium">{item.projectMilestone}</td>
                    <td>
                      <span className="tag tag-info tag-dot">
                        {String(item.defaultTimelineStatus || 'planned').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          className="icon-btn"
                          title="Edit"
                          style={{ color: 'var(--info)' }}
                          onClick={() => {
                            setModalOpen('update');
                            setProjectMilestoneData(item);
                          }}
                        >
                          <i className="ti ti-edit" />
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => {
                            setModalOpen('delete');
                            setProjectMilestoneData(item);
                          }}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Pagination
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              totalPage={totalPage}
              totalItems={projectMilestoneCount}
            />
          </>
        )}
      </div>

      {modalOpen === 'add' && (
        <AddNewProjectMilestone setModalOpen={setModalOpen} getProjectMilestoneData={getProjectMilestoneData} />
      )}
      {modalOpen === 'update' && (
        <UpdateProjectMilestone
          projectMilestoneData={projectMilestoneData}
          setModalOpen={setModalOpen}
          getProjectMilestoneData={getProjectMilestoneData}
          setConfirm={setConfirm}
          confirm={confirm}
        />
      )}
      {modalOpen === 'delete' && (
        <DeleteProjectMilestone
          getProjectMilestoneData={getProjectMilestoneData}
          getDeletedProjectMilestoneData={getDeletedProjectMilestoneData}
          setModalOpen={setModalOpen}
          projectMilestoneData={projectMilestoneData}
        />
      )}
      {modalOpen === 'deletedHistiory' && (
        <DeletedProjectMilestoneHistory
          getProjectMilestoneData={getProjectMilestoneData}
          getDeletedProjectMilestoneData={getDeletedProjectMilestoneData}
          setModalOpen={setModalOpen}
          deletedPageNumber={deletedPageNumber}
          setDeletedPageNumber={setDeletedPageNumber}
          deletedItemsPerPage={deletedItemsPerPage}
          setDeletedItemsPerPage={setDeletedItemsPerPage}
          deleteProjectMilestoneList={deleteProjectMilestoneList}
          deleteProjectMilestoneCount={deleteProjectMilestoneCount}
        />
      )}
    </div>
  );
}

export default ProjectMilestoneManagement;
